import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import { COUNTRY } from "config";

import dayjs from "dayjs";
import { setPrefillData } from "utils/utils";

interface QuotesState {
  availableQuotes: Quote[];
  invalidQuotes: Quote[];
  selectedQuotes: Quote[];
  selectedQuotesForSendingEmail: Quote[];
  sendingQuotes: boolean;
  sentQuotesToken: string;
  sentQuotesError: string;
  status: string;
  polling: boolean;
  is_add_on: boolean;
  visiblePlanNames: string[];
}

const initialState: QuotesState = {
  availableQuotes: [],
  invalidQuotes: [],
  selectedQuotes: [],
  selectedQuotesForSendingEmail: [],
  sendingQuotes: false,
  sentQuotesToken: null,
  sentQuotesError: null,
  status: "",
  polling: false,
  is_add_on: false,
  visiblePlanNames: [],
};

// Create a helper function to check if a quote is in the Silversky context

const quotesSlice = createSlice({
  name: "quotes",
  initialState,
  reducers: {
    setAvailableQuotes: (state, action: PayloadAction<Quote[]>) => {
      state.availableQuotes = action.payload;
    },
    setInvalidQuotes: (state, action: PayloadAction<Quote[]>) => {
      state.invalidQuotes = action.payload;
    },
    addOrRemoveQuote: (state, action: PayloadAction<Quote>) => {
      // Temporary condition change from COUNTRY === "sg" && action.payload.product_details?.type === "PET" to false
      if (false) {
        const mainPlans = ["Silver", "Gold", "Platinum"];
        const isMainPlan = mainPlans.includes(
          action.payload.premium?.additional_info?.plan_name?.default
        );
        const planName =
          action.payload.premium?.additional_info?.plan_name?.default;

        if (planName && !state.visiblePlanNames.includes(planName)) {
          state.visiblePlanNames.push(planName);
        }
        const isKnockoutFlow = window.location.pathname.includes("/knockout");

        if (state.selectedQuotes.find((q) => q.id === action.payload.id)) {
          state.selectedQuotes = [];
          if (isKnockoutFlow) {
            state.is_add_on = false;
            setPrefillData({ is_add_on: "FALSE" });
          }
        } else if (isMainPlan) {
          const matchingAddOn = state.availableQuotes.find(
            (quote) =>
              !mainPlans.includes(
                quote.premium?.additional_info?.plan_name?.default
              ) &&
              quote.premium?.additional_info?.plan_name?.default ===
                action.payload.premium?.additional_info?.plan_name?.default
          );

          if (matchingAddOn) {
            state.selectedQuotes = [action.payload, matchingAddOn];
            if (isKnockoutFlow) {
              state.is_add_on = true;
              setPrefillData({ is_add_on: "TRUE" });
            }
          } else {
            state.selectedQuotes = [action.payload];
            if (isKnockoutFlow) {
              state.is_add_on = true;
              setPrefillData({ is_add_on: "TRUE" });
            }
          }
        }
      } else {
        if (state.selectedQuotes.find((q) => q.id === action.payload.id)) {
          state.selectedQuotes = state.selectedQuotes.filter(
            (q) => q.id !== action.payload.id
          );
        } else {
          state.selectedQuotes = state.selectedQuotes.filter(
            (q) =>
              q?.product_details?.type !== action.payload.product_details?.type
          );
          state.selectedQuotes = [...state.selectedQuotes, action.payload];
        }
      }
    },
    addOrRemoveAddOn: (state, action: PayloadAction<Quote>) => {
      if (COUNTRY === "sg" && action.payload.product_details?.type === "PET") {
        const mainPlans = ["Silver", "Gold", "Platinum"];
        const mainPlan = state.selectedQuotes.find((q) =>
          mainPlans.includes(q.premium?.additional_info?.plan_name?.default)
        );

        if (!mainPlan) return;

        const mainPlanTier =
          mainPlan.premium?.additional_info?.plan_name?.default;
        const addOnTier =
          action.payload.premium?.additional_info?.plan_name?.default;

        if (mainPlanTier !== addOnTier) return;

        const isAddOnSelected = state.selectedQuotes.some(
          (q) => q.id === action.payload.id
        );

        const isKnockoutFlow = window.location.pathname.includes("/knockout");

        if (
          isKnockoutFlow &&
          !state.is_add_on &&
          mainPlan &&
          addOnTier === mainPlanTier
        ) {
          setPrefillData({ is_add_on: "TRUE" });
          state.is_add_on = true;
          return;
        }

        if (isAddOnSelected) {
          state.is_add_on = false;
          setPrefillData({ is_add_on: "FALSE" });
        } else {
          state.selectedQuotes = [mainPlan, action.payload];
          setPrefillData({ is_add_on: "TRUE" });
          state.is_add_on = true;
        }
      }
    },
    removeSelectedQuote: (state, action: PayloadAction<Quote>) => {
      state.selectedQuotes = state.selectedQuotes.filter(
        (q) => action.payload.id !== q.id
      );
    },
    addOrRemoveQuoteForSendingEmail: (state, action: PayloadAction<Quote>) => {
      if (
        state.selectedQuotesForSendingEmail.find(
          (q) => q.id === action.payload.id
        )
      ) {
        state.selectedQuotesForSendingEmail =
          state.selectedQuotesForSendingEmail.filter(
            (q) => q.id !== action.payload.id
          );
      } else {
        state.selectedQuotesForSendingEmail = [
          ...state.selectedQuotesForSendingEmail,
          action.payload,
        ];
      }
    },
    sendQuotes: (state, action: PayloadAction<SendQuotesPayload>) => {
      state.sendingQuotes = true;
    },
    sendQuoteForReview: (
      state,
      action: PayloadAction<SendQuoteForReviewPayload>
    ) => {
      state.sendingQuotes = true;
    },
    setSentQuotesToken: (state, action: PayloadAction<string>) => {
      state.sentQuotesToken = action.payload;
    },
    setSentQuotesError: (state, action: PayloadAction<string>) => {
      state.sentQuotesError = action.payload;
    },
    setSendingQuotes: (state, action: PayloadAction<boolean>) => {
      state.sendingQuotes = action.payload;
    },
    resetQuotes: () => initialState,
    resetSelectedQuotes: (state) => {
      state.selectedQuotes = [];
      if (COUNTRY === "sg") {
        const isKnockoutFlow = window.location.pathname.includes("/knockout");
        if (isKnockoutFlow) {
          state.is_add_on = false;
        }
      }
    },
    resetQuotesForSendingEmail: (state) => {
      state.selectedQuotesForSendingEmail = [];
    },
    setStatus: (state, action: PayloadAction<string>) => {
      state.status = action.payload;
    },
    setPolling: (state, action: PayloadAction<boolean>) => {
      state.polling = action.payload;
    },
    setIsAddOn: (state, action: PayloadAction<boolean>) => {
      state.is_add_on = action.payload;
    },
    initializeVisiblePlanNames: (state, action: PayloadAction<Quote[]>) => {
      const planNames = action.payload
        .map((quote) => quote.premium?.additional_info?.plan_name?.default)
        .filter((name): name is string => !!name);
      state.visiblePlanNames = Array.from(new Set(planNames));
    },
  },
});

export const {
  setAvailableQuotes,
  setInvalidQuotes,
  addOrRemoveQuote,
  addOrRemoveAddOn,
  addOrRemoveQuoteForSendingEmail,
  resetQuotesForSendingEmail,
  sendQuotes,
  sendQuoteForReview,
  setSendingQuotes,
  setSentQuotesToken,
  setSentQuotesError,
  resetQuotes,
  resetSelectedQuotes,
  removeSelectedQuote,
  setStatus,
  setPolling,
  setIsAddOn,
  initializeVisiblePlanNames,
} = quotesSlice.actions;

export default quotesSlice.reducer;
